import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Stack,
  Switch,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery } from "@apollo/client";

import {
  CURRENT_USER_QUERY,
  MY_RECRUITER_NOTIFICATION_SETTINGS_QUERY,
  SET_MY_RECRUITER_NOTIFICATION_SETTINGS_MUTATION,
} from "../graphql/users";

export const DEFAULT_TIMEZONE = "America/New_York";

// IANA names offered in the timezone picker. The saved value is added if it
// isn't one of these, so a timezone set elsewhere is never silently lost.
export const TIMEZONES = [
  { value: "America/New_York", label: "US Eastern (New York)" },
  { value: "America/Chicago", label: "US Central (Houston, Chicago)" },
  { value: "America/Denver", label: "US Mountain (Denver)" },
  { value: "America/Los_Angeles", label: "US Pacific (Los Angeles)" },
  { value: "America/Anchorage", label: "Alaska (Anchorage)" },
  { value: "Pacific/Honolulu", label: "Hawaii (Honolulu)" },
  { value: "America/Edmonton", label: "Canada Mountain (Calgary)" },
  { value: "America/Halifax", label: "Canada Atlantic (Halifax)" },
  { value: "America/St_Johns", label: "Newfoundland (St. John's)" },
  { value: "America/Sao_Paulo", label: "Brazil (São Paulo)" },
  { value: "Europe/London", label: "UK (London, Aberdeen)" },
  { value: "Europe/Oslo", label: "Norway (Oslo, Stavanger)" },
  { value: "Europe/Amsterdam", label: "Netherlands (Amsterdam)" },
  { value: "Europe/Paris", label: "France (Paris)" },
  { value: "Africa/Lagos", label: "West Africa (Lagos)" },
  { value: "Asia/Riyadh", label: "Saudi Arabia (Riyadh)" },
  { value: "Asia/Qatar", label: "Qatar (Doha)" },
  { value: "Asia/Dubai", label: "UAE (Dubai, Abu Dhabi)" },
  { value: "Asia/Singapore", label: "Singapore" },
  { value: "Australia/Perth", label: "Western Australia (Perth)" },
  { value: "Australia/Brisbane", label: "Queensland (Brisbane)" },
];

const HOURS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? "AM" : "PM"}`,
}));

const TOGGLES = [
  {
    field: "shortlistAlertEnabled",
    label: "Shortlist alerts",
    help: "When someone else shortlists a candidate on one of your jobs.",
  },
  {
    field: "roleOverviewEnabled",
    label: "Role overviews",
    help: "Mya's recommended candidates for one of your jobs.",
  },
  {
    field: "reviewReminderEnabled",
    label: "Review reminders",
    help: "When the ATS import holds one of your jobs for review instead of publishing it.",
  },
];

const FIELDS = [
  "digestEnabled",
  "dailySendHour",
  "timezone",
  "shortlistAlertEnabled",
  "roleOverviewEnabled",
  "reviewReminderEnabled",
];

function pickSettings(settings) {
  return Object.fromEntries(FIELDS.map((f) => [f, settings[f]]));
}

function SettingSwitch({ id, label, help, isChecked, onChange }) {
  return (
    <FormControl display="flex" alignItems="flex-start" gap={3}>
      <Switch
        id={id}
        mt={1}
        colorScheme="teal"
        isChecked={isChecked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <Flex direction="column">
        <FormLabel htmlFor={id} mb="0">
          {label}
        </FormLabel>
        <Text fontSize="sm" color="gray.400">
          {help}
        </Text>
      </Flex>
    </FormControl>
  );
}

// "Email notifications" section of Account Settings: the recruiter-level
// settings for the emails the API sends. Renders nothing for non-recruiters,
// nor until the settings say they apply (active: the recruiter's agency gets
// its emails from the API; while it is on the Metabase alerts nothing here
// would be sent).
// New-applicant emails are per job and live on the job's "Notify me" panel.
export default function RecruiterEmailNotificationSettings() {
  const toast = useToast();
  const { data: userData } = useQuery(CURRENT_USER_QUERY);
  const isRecruiter = userData?.currentUser?.role === "Recruiter";

  const { data, loading, error } = useQuery(
    MY_RECRUITER_NOTIFICATION_SETTINGS_QUERY,
    { skip: !isRecruiter }
  );
  const [saveSettings, { loading: saving }] = useMutation(
    SET_MY_RECRUITER_NOTIFICATION_SETTINGS_MUTATION,
    {
      // The settings have no id, so write them back to the query by hand.
      update(cache, { data: result }) {
        cache.writeQuery({
          query: MY_RECRUITER_NOTIFICATION_SETTINGS_QUERY,
          data: {
            myRecruiterNotificationSettings:
              result.setMyRecruiterNotificationSettings,
          },
        });
      },
    }
  );

  const saved = data?.myRecruiterNotificationSettings;
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (saved) setForm(pickSettings(saved));
  }, [saved]);

  const timezones = useMemo(() => {
    const tz = form?.timezone;
    if (!tz || TIMEZONES.some((t) => t.value === tz)) return TIMEZONES;
    return [{ value: tz, label: tz }, ...TIMEZONES];
  }, [form?.timezone]);

  if (!isRecruiter || !saved?.active) return null;

  const set = (field) => (value) => setForm({ ...form, [field]: value });

  const onSave = async () => {
    try {
      await saveSettings({ variables: { input: form } });
      toast({ title: "Email settings saved", status: "success" });
    } catch (e) {
      toast({
        title: "Couldn't save email settings",
        description: e?.message,
        status: "error",
        isClosable: true,
      });
    }
  };

  return (
    <Stack
      id="email-notifications"
      spacing={4}
      mt="12"
      data-testid="recruiter-email-settings"
    >
      <Text fontSize="lg" fontWeight="bold">
        Email notifications
      </Text>
      {loading && <Text color="gray.400">Loading…</Text>}
      {error && (
        <Text color="red.400">Couldn't load your email settings.</Text>
      )}
      {form && (
        <>
          <SettingSwitch
            id="digestEnabled"
            label="Daily digest"
            help="One email a day covering all of your jobs."
            isChecked={form.digestEnabled}
            onChange={set("digestEnabled")}
          />
          {form.digestEnabled && (
            <Flex gap={3} pl={12} wrap="wrap">
              <FormControl w="auto">
                <FormLabel htmlFor="dailySendHour" fontSize="sm" mb={1}>
                  Arrives at
                </FormLabel>
                <Select
                  id="dailySendHour"
                  size="sm"
                  w="120px"
                  value={form.dailySendHour}
                  onChange={(e) =>
                    set("dailySendHour")(Number(e.target.value))
                  }
                >
                  {HOURS.map((h) => (
                    <option key={h.value} value={h.value}>
                      {h.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl w="auto">
                <FormLabel htmlFor="timezone" fontSize="sm" mb={1}>
                  Timezone
                </FormLabel>
                <Select
                  id="timezone"
                  size="sm"
                  value={form.timezone || DEFAULT_TIMEZONE}
                  onChange={(e) => set("timezone")(e.target.value)}
                >
                  {timezones.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Flex>
          )}
          {TOGGLES.map((t) => (
            <SettingSwitch
              key={t.field}
              id={t.field}
              label={t.label}
              help={t.help}
              isChecked={form[t.field]}
              onChange={set(t.field)}
            />
          ))}
          <Text fontSize="sm" color="gray.400">
            New-applicant emails are set per job, from the job's "Notify me"
            menu.
          </Text>
          <Button width="100%" onClick={onSave} isLoading={saving}>
            Save email settings
          </Button>
        </>
      )}
    </Stack>
  );
}
